import { Loader2, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useAddTagsToParentMutation } from '~entities/parent/api/add-tags-to-parent';
import { useGetParentTags } from '~entities/parent/api/get-parent-tags';
import { useSyncParentTagsMutation } from '~entities/parent/api/sync-parent-tags';

import { type TagsOption, TagsSelector } from '~features/shared/tags-selector';

import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Label } from '~shared/shadcn/ui/label';

import { RemoveTagFromParent } from './remove-tag-from-parent';

interface ParentTagsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentId: string;
  parentName: string;
  isDemoParent?: boolean;
}

export const ParentTags = ({
  open,
  onOpenChange,
  parentId,
  parentName,
  isDemoParent = false,
}: ParentTagsProps) => {
  const [selectedTags, setSelectedTags] = useState<Array<TagsOption>>([]);

  const { data, isFetching } = useGetParentTags({ parentId });

  const { mutateAsync: addTagsToParent, isPending } =
    useAddTagsToParentMutation();

  const { mutateAsync: syncParentTags, isPending: isSyncing } =
    useSyncParentTagsMutation();

  const handleAddTagsToParent = async () => {
    try {
      await addTagsToParent({
        parentId,
        tagIds: selectedTags.map((tag) => tag.value),
      });
      toast.success('Tags added to parent successfully!');
      onOpenChange(false);
    } catch (error) {
      console.error(error);
      toast.error('Failed to add tags to parent');
    }
  };

  const handleSyncTags = async () => {
    try {
      await syncParentTags({ parentId });
      toast.success(
        isDemoParent
          ? 'Demo family tags are portal-only (no CRM sync needed).'
          : 'Tags synced successfully!',
      );
    } catch (error) {
      console.error(error);
      toast.error('Failed to sync tags');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='overflow-visible'>
        <DialogHeader>
          <DialogTitle>Parent Tags</DialogTitle>
        </DialogHeader>
        <div className='mb-6 flex flex-col gap-4'>
          <div className='flex items-center justify-between'>
            <Label>{parentName}</Label>
            {!isDemoParent && (
              <Button
                onClick={() => void handleSyncTags()}
                disabled={isSyncing}
                variant='outline'
                size='sm'
              >
                {isSyncing ? (
                  <Loader2 className='animate-spin' />
                ) : (
                  <RefreshCw />
                )}{' '}
                Sync Tags
              </Button>
            )}
          </div>
          <div className='flex flex-wrap gap-2'>
            {isFetching ? (
              <div className='flex w-full items-center justify-center'>
                <Loader2 className='animate-spin' />
              </div>
            ) : (
              <>
                {data && data.length > 0 ? (
                  data.map((tag) => (
                    <Badge
                      key={tag._id}
                      className='group flex items-center gap-2 hover:bg-primary hover:text-primary-foreground'
                    >
                      {tag.fieldName}
                      <RemoveTagFromParent
                        parentId={parentId}
                        tagName={tag.fieldName}
                        tagId={tag._id}
                      />
                    </Badge>
                  ))
                ) : (
                  <div className='flex w-full items-center justify-center'>
                    <p className='text-center text-sm text-muted-foreground'>
                      No tags found
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <TagsSelector
          value={selectedTags}
          onChange={(value) => {
            setSelectedTags(value ?? []);
          }}
          isOptionDisabled={(option) => {
            if (!data) return false;
            return data.some((tag) => tag._id === option.value);
          }}
          menuPortalTarget={null}
        />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline'>Cancel</Button>
          </DialogClose>
          <Button
            onClick={() => void handleAddTagsToParent()}
            disabled={isPending || selectedTags.length === 0}
          >
            {isPending && <Loader2 className='animate-spin' />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
